import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Pressable } from 'react-native';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { palettes } from '../config';
import { getResponsiveStyle } from '../utils/getResponsiveStyle';
import { Button, GoBackButton } from '../components';
import Icon from 'react-native-vector-icons/Feather';
import { useLazyQuery, gql } from '@apollo/client';
import { useSetAlert } from '../hooks/useSetAlerts';
import DesktopTemplate from '../components/common/templates/DesktopTemplate';
// Define the new query
const GET_EVENT_ANALYTICS = gql`
  query getEventAnalytics($eventId: ID!) {
    getEventAnalytics(input: {
      eventId: $eventId
    }) {
      statusCode
      data {
        ... on EventMetricsAggregation {
          totalViewers
          totalCartAdditions
          totalClicks
          totalRegistrations
        }
      }
    }
  }
`;

type RouteParams = {
    EventAnalytics: {
        eventId: string;
    };
};

const EventAnalytics = () => {
    const route = useRoute<RouteProp<RouteParams, 'EventAnalytics'>>();
    const { eventId } = route.params;
    const navigation = useNavigation<any>();
    const { setAlert } = useSetAlert();
    const [loading, setLoading] = useState(true);

    // Analytics data structure
    const [analytics, setAnalytics] = useState({
        viewers: 0,
        clicks: 0,
        cartAdditions: 0,
        registrations: 0
    });

    const [getEventAnalytics, { data: analyticsData, loading: analyticsLoading }] = useLazyQuery(GET_EVENT_ANALYTICS, {
        variables: { eventId },
        onCompleted: (data) => {
            console.log({ data });
            if (data?.getEventAnalytics?.data) {
                // Extract real analytics data
                const metrics = data?.getEventAnalytics?.data;
                setAnalytics({
                    viewers: metrics?.totalViewers || 0,
                    cartAdditions: metrics?.totalCartAdditions || 0,
                    clicks: metrics?.totalClicks || 0,
                    registrations: metrics?.totalRegistrations || 0
                });
                setLoading(false);
            } else {
                setAlert('Failed to load event analytics data', 'danger');
                setLoading(false);
            }
        },
        onError: (error) => {
            setAlert(error.message, 'danger');
            setLoading(false);
        }
    });

    useEffect(() => {
        getEventAnalytics();
    }, [eventId]);

    // Navigate to registrations page
    const handleNavigateToRegistrations = () => {
        navigation.navigate('EventRegistrations', { eventId });
    };

    // Since we may not have event name in the new API, we'll use a static title
    const eventName = 'Event Analytics';

    return (
        <DesktopTemplate>
            <View style={styles.header}>
                <GoBackButton />
                <Text style={styles.headerTitle}>{eventName}</Text>
            </View>

            {loading || analyticsLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={palettes.primary[0]} />
                    <Text style={styles.loadingText}>Loading analytics data...</Text>
                </View>
            ) : (
                <View style={styles.analyticsContainer}>
                    <Text style={styles.analyticsTitle}>Event Performance</Text>
                    
                    <View style={styles.statCard}>
                        <View style={styles.statIconContainer}>
                            <Icon name="eye" size={24} color={palettes.primary[0]} />
                        </View>
                        <View style={styles.statContent}>
                            <Text style={styles.statTitle}>Viewers</Text>
                            <Text style={styles.statValue}>{analytics.viewers}</Text>
                            <Text style={styles.statDescription}>Total number of people who viewed the event</Text>
                        </View>
                    </View>

                    <View style={styles.statCard}>
                        <View style={styles.statIconContainer}>
                            <Icon name="mouse-pointer" size={24} color={palettes.primary[0]} />
                        </View>
                        <View style={styles.statContent}>
                            <Text style={styles.statTitle}>Clicks</Text>
                            <Text style={styles.statValue}>{analytics.clicks}</Text>
                            <Text style={styles.statDescription}>Total interactions during the live session</Text>
                        </View>
                    </View>

                    <View style={styles.statCard}>
                        <View style={styles.statIconContainer}>
                            <Icon name="shopping-cart" size={24} color={palettes.primary[0]} />
                        </View>
                        <View style={styles.statContent}>
                            <Text style={styles.statTitle}>Cart Additions</Text>
                            <Text style={styles.statValue}>{analytics.cartAdditions}</Text>
                            <Text style={styles.statDescription}>Products added to cart during the event</Text>
                        </View>
                    </View>

                    <Pressable onPress={handleNavigateToRegistrations}>
                        <View style={styles.statCard}>
                            <View style={styles.statIconContainer}>
                                <Icon name="user-plus" size={24} color={palettes.primary[0]} />
                            </View>
                            <View style={styles.statContent}>
                                <Text style={styles.statTitle}>Registrations</Text>
                                <Text style={styles.statValue}>{analytics.registrations}</Text>
                                <Text style={styles.statDescription}>Users who registered during the live session</Text>
                            </View>
                            <View style={styles.arrowContainer}>
                                <Icon name="chevron-right" size={20} color={palettes.primary[0]} />
                            </View>
                        </View>
                    </Pressable>
                </View>
            )}
        </DesktopTemplate>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: palettes.light[0],
    },
    header: {
        padding: getResponsiveStyle(16),
        backgroundColor: palettes.light[0],
        borderBottomWidth: 1,
        borderBottomColor: palettes.grey[5],
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: getResponsiveStyle(18, 'font'),
        fontWeight: '700',
        color: palettes.dark[0],
        marginLeft: getResponsiveStyle(16),
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: getResponsiveStyle(32),
        marginTop: getResponsiveStyle(40),
    },
    loadingText: {
        marginTop: getResponsiveStyle(16),
        fontSize: getResponsiveStyle(14, 'font'),
        color: palettes.grey[1],
    },
    analyticsContainer: {
        padding: getResponsiveStyle(16),
    },
    analyticsTitle: {
        fontSize: getResponsiveStyle(20, 'font'),
        fontWeight: '700',
        color: palettes.dark[0],
        marginBottom: getResponsiveStyle(24),
    },
    statCard: {
        backgroundColor: 'white',
        borderRadius: 8,
        padding: getResponsiveStyle(16),
        marginBottom: getResponsiveStyle(16),
        flexDirection: 'row',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 2,
    },
    statIconContainer: {
        width: getResponsiveStyle(50),
        height: getResponsiveStyle(50),
        borderRadius: 25,
        backgroundColor: palettes.grey[6],
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: getResponsiveStyle(16),
    },
    statContent: {
        flex: 1,
    },
    statTitle: {
        fontSize: getResponsiveStyle(16, 'font'),
        fontWeight: '600',
        color: palettes.grey[1],
        marginBottom: getResponsiveStyle(4),
    },
    statValue: {
        fontSize: getResponsiveStyle(24, 'font'),
        fontWeight: '700',
        color: palettes.dark[0],
        marginBottom: getResponsiveStyle(4),
    },
    statDescription: {
        fontSize: getResponsiveStyle(14, 'font'),
        color: palettes.grey[1],
    },
    arrowContainer: {
        justifyContent: 'center',
        alignItems: 'center',
    },
});

export default EventAnalytics; 